/**
 * SA-1328 (design §3, §3.1): the process list grouped into passes. A pass is
 * a run of visits that never goes back; a re-open or a revision ends it and
 * is a hinge, and so is a move back along an exit ("sent back") or one
 * nothing explains ("went back"). Every pass but the current one is one fold
 * row; the current pass shows its last WINDOW_BEFORE visits and folds the
 * rest. Pure, and by order alone: no time decides a boundary.
 */
import { exitWord } from '@/components/process/stepDrawerText'
import { spanText, whenText } from '@/components/process/stepLiveText'
import type { Timeline, TimelineEntry } from './timelineModel'
import { earlierWord, foldRange, sentBackHeading, stepsWord, wentBackHeading } from './passesText'

export const WINDOW_BEFORE = 3

export type HingeKind = 'reopened' | 'revised' | 'sent_back' | 'went_back'
export interface Hinge { key: string; kind: HingeKind; stepId: string | null; heading: string; meta: string; produced: string | null }
export interface Fold { id: string; entries: TimelineEntry[]; earlier: boolean; range: string; span: string | null; count: string }
export type PassRow =
  | { kind: 'entry'; entry: TimelineEntry }
  | { kind: 'hinge'; hinge: Hinge }
  | { kind: 'fold'; fold: Fold }
export interface PassView { rows: PassRow[]; more: number }

/** The index a visit reads as when ordering: a step the definition lacks reads −1. */
const index = (e: TimelineEntry) => e.number ?? -1

function foldOf(id: string, entries: TimelineEntry[], earlier: boolean, count: string, now?: Date): Fold {
  const first = entries[0]
  const last = entries[entries.length - 1]
  return { id, entries, earlier, range: foldRange(first, last), span: spanText(first.at, last.at, now), count }
}

/** The hinge between a pass ending at `last` and a move back to `next`. */
function derivedHinge(last: TimelineEntry, next: TimelineEntry, now?: Date): Hinge {
  const key = `turn#${last.key}`
  if (last.exitTarget && last.exitTarget.number <= index(next)) {
    return {
      key, kind: 'sent_back', stepId: last.stepId,
      heading: sentBackHeading(last.heading, last.exitTarget.heading),
      meta: [whenText(last.at, now), last.exit ? exitWord(last.exit) : null].filter(Boolean).join(' · '),
      produced: last.produced,
    }
  }
  return { key, kind: 'went_back', stepId: next.stepId, heading: wentBackHeading(next.heading), meta: whenText(last.at, now) ?? '', produced: null }
}

export function passView(timeline: Timeline, now?: Date): PassView {
  type Item = { pass: TimelineEntry[] } | { hinge: Hinge }
  const items: Item[] = []
  let open: TimelineEntry[] = []
  const flush = () => {
    if (open.length) items.push({ pass: open })
    open = []
  }
  const tail: TimelineEntry[] = []
  for (const e of timeline.entries) {
    if (e.state === 'reopened' || e.state === 'revised') {
      flush()
      items.push({ hinge: { key: e.key, kind: e.state, stepId: e.stepId, heading: e.heading, meta: e.meta, produced: null } })
    } else if (e.state === 'done') {
      const last = open[open.length - 1]
      if (!last || index(e) >= index(last)) {
        open.push(e)
      } else if (e.number === null) {
        // A step the definition lacks starts its own pass, with no hinge before it.
        flush()
        open = [e]
      } else {
        flush()
        items.push({ hinge: derivedHinge(last, e, now) })
        open = [e]
      }
    } else {
      // The current step counts as the next visit for the last boundary.
      const last = open[open.length - 1]
      if (e.state === 'current' && last && e.number !== null && index(e) < index(last)) {
        flush()
        items.push({ hinge: derivedHinge(last, e, now) })
      }
      tail.push(e)
    }
  }

  const rows: PassRow[] = []
  let ordinal = 0
  for (const item of items) {
    if ('hinge' in item) rows.push({ kind: 'hinge', hinge: item.hinge })
    else rows.push({ kind: 'fold', fold: foldOf(`pass#${++ordinal}`, item.pass, false, stepsWord(item.pass.length), now) })
  }
  if (open.length > WINDOW_BEFORE) {
    const earlier = open.slice(0, open.length - WINDOW_BEFORE)
    rows.push({ kind: 'fold', fold: foldOf('earlier', earlier, true, earlierWord(earlier.length), now) })
    open = open.slice(open.length - WINDOW_BEFORE)
  }
  for (const entry of open) rows.push({ kind: 'entry', entry })
  for (const entry of tail) rows.push({ kind: 'entry', entry })
  return { rows, more: timeline.more }
}
